import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@descope/react-sdk';
import { setActiveTenantId } from '../services/api';

const STORAGE_KEY = 'brandlight.tenantId';
const NAME_STORAGE_KEY = 'brandlight.tenantName';

interface TenantContextValue {
  /** Currently active DB tenant id (sent as X-Tenant-Id). Null until the user picks one. */
  activeTenantId: number | null;
  /** Display name of the active tenant, persisted alongside the id. */
  activeTenantName: string | null;
  /** Switch to a different tenant. Invalidates the TanStack Query cache. */
  switchTenant: (tenantId: number, tenantName?: string) => void;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

function readPersistedId(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  } catch {
    return null;
  }
}

function readPersistedName(): string | null {
  try {
    return localStorage.getItem(NAME_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isSessionLoading } = useSession();
  const queryClient = useQueryClient();

  // Always read from localStorage on init — Descope's isAuthenticated is false
  // on the first render (async hydration), so we can't gate on it here.
  // The logout effect below clears storage when the session ends.
  const [activeTenantId, setActiveTenantIdState] = useState<number | null>(readPersistedId);
  const [activeTenantName, setActiveTenantNameState] = useState<string | null>(readPersistedName);

  // Keep the api interceptor in sync whenever the active tenant changes.
  useEffect(() => {
    setActiveTenantId(activeTenantId);
  }, [activeTenantId]);

  // Clear tenant state on logout. Guard with isSessionLoading so we don't
  // clear on the initial render when isAuthenticated is still false while
  // Descope hydrates the session from storage.
  useEffect(() => {
    if (!isSessionLoading && !isAuthenticated) {
      setActiveTenantIdState(null);
      setActiveTenantNameState(null);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(NAME_STORAGE_KEY);
    }
  }, [isAuthenticated, isSessionLoading]);

  const switchTenant = useCallback(
    (tenantId: number, tenantName?: string) => {
      localStorage.setItem(STORAGE_KEY, String(tenantId));
      if (tenantName !== undefined) {
        localStorage.setItem(NAME_STORAGE_KEY, tenantName);
      }
      setActiveTenantIdState(tenantId);
      setActiveTenantNameState(tenantName ?? null);
      // Blow away the cache so every query re-fetches under the new tenant.
      queryClient.removeQueries();
    },
    [queryClient],
  );

  const value = useMemo<TenantContextValue>(
    () => ({ activeTenantId, activeTenantName, switchTenant }),
    [activeTenantId, activeTenantName, switchTenant],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside TenantProvider');
  return ctx;
}
